import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, ChartContainer } from '@/components/charts';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useBatteryMonitoring, useBatteryMonitoringAccounts } from '../hooks';
import {
  RefreshCw, Battery, BatteryWarning, BatteryFull,
  Zap, Search, ChevronLeft, ChevronRight,
  AlertTriangle, ArrowUpDown, Check, ChevronsUpDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BatteryMonitoringFilters, BatteryMonitoringItem } from '../types';

type VoltagePreset = 'all' | 'critical' | 'low' | 'warning' | 'healthy';

function getVoltageColor(voltage: number): string {
  if (voltage < 2.5) return 'text-red-600 font-semibold';
  if (voltage < 3.0) return 'text-orange-600 font-semibold';
  if (voltage < 3.3) return 'text-yellow-600 font-semibold';
  return 'text-green-600';
}

function getBaseColorBadgeClass(color: string): string {
  const lc = color?.toLowerCase() || '';
  if (lc.includes('yellow')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  if (lc.includes('blue')) return 'bg-blue-100 text-blue-800 border-blue-300';
  if (lc.includes('green')) return 'bg-green-100 text-green-800 border-green-300';
  if (lc.includes('red')) return 'bg-red-100 text-red-800 border-red-300';
  if (lc.includes('white')) return 'bg-gray-50 text-gray-800 border-gray-300';
  if (lc.includes('black')) return 'bg-gray-800 text-white border-gray-600';
  return 'bg-gray-100 text-gray-800 border-gray-300';
}

function useRelativeTimeFormatter() {
  const { t } = useTranslation('intelligence');
  return (dateStr?: string): string => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return t('battery.time.justNow');
    if (diffMins < 60) return t('battery.time.minutesAgo', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('battery.time.hoursAgo', { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return t('battery.time.daysAgo', { count: diffDays });
    return t('battery.time.monthsAgo', { count: Math.floor(diffDays / 30) });
  };
}

export default function BatteryMonitoringPage() {
  const { t } = useTranslation('intelligence');
  const formatRelativeTime = useRelativeTimeFormatter();

  const [filters, setFilters] = useState<BatteryMonitoringFilters>({
    page: 1,
    limit: 25,
    sort_by: 'battery_voltage',
    sort_order: '1',
    power_source: 'all',
    status: 'all',
  });
  const [searchInput, setSearchInput] = useState('');
  const [voltagePreset, setVoltagePreset] = useState<VoltagePreset>('all');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('all');
  const [accountComboboxOpen, setAccountComboboxOpen] = useState(false);

  const { data, isLoading, refetch, isFetching } = useBatteryMonitoring(filters);
  const { data: accounts = [], isLoading: accountsLoading } = useBatteryMonitoringAccounts();

  // Get selected account name for display
  const selectedAccountName = selectedAccountId === 'all'
    ? t('battery.filters.allAccounts')
    : accounts.find(a => a.id === selectedAccountId)?.name || selectedAccountId;

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    setAccountComboboxOpen(false);
    if (accountId === 'all') {
      setFilters(f => ({ ...f, organisation: undefined, page: 1 }));
    } else {
      const account = accounts.find(a => a.id === accountId);
      setFilters(f => ({
        ...f,
        organisation: account?.mongoOrganisationId || undefined,
        page: 1,
      }));
    }
  };

  const summary = data?.summary;
  const items = data?.items || [];
  const pagination = data?.pagination;

  const BASE_COLOR_HEX: Record<string, string> = {
    yellow: '#eab308',
    blue: '#3b82f6',
    green: '#22c55e',
    red: '#ef4444',
    white: '#d1d5db',
    black: '#374151',
    grey: '#9ca3af',
    gray: '#9ca3af',
    orange: '#f97316',
    purple: '#a855f7',
  };

  const baseColorChartData = useMemo(() => {
    return (summary?.by_base_color || []).map(b => ({
      name: b.color || 'Unknown',
      value: b.count,
    }));
  }, [summary?.by_base_color]);

  const baseColorChartColors = useMemo(() => {
    return (summary?.by_base_color || []).map(b => {
      const lc = (b.color || '').toLowerCase();
      return BASE_COLOR_HEX[lc] || '#6b7280';
    });
  }, [summary?.by_base_color]);

  const voltageDistributionData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: t('battery.levels.critical'), value: summary.critical_count },
      { name: t('battery.levels.low'), value: summary.low_count },
      { name: t('battery.levels.warning'), value: summary.warning_count },
      { name: t('battery.levels.healthy'), value: summary.healthy_count },
    ].filter(d => d.value > 0);
  }, [summary, t]);

  const applyVoltagePreset = (preset: VoltagePreset) => {
    setVoltagePreset(preset);
    switch (preset) {
      case 'critical':
        setFilters(f => ({ ...f, page: 1, voltage_min: undefined, voltage_max: 2.5 }));
        break;
      case 'low':
        setFilters(f => ({ ...f, page: 1, voltage_min: 2.5, voltage_max: 3.0 }));
        break;
      case 'warning':
        setFilters(f => ({ ...f, page: 1, voltage_min: 3.0, voltage_max: 3.3 }));
        break;
      case 'healthy':
        setFilters(f => ({ ...f, page: 1, voltage_min: 3.3, voltage_max: undefined }));
        break;
      default:
        setFilters(f => ({ ...f, page: 1, voltage_min: undefined, voltage_max: undefined }));
    }
  };

  const applySearch = () => {
    setFilters(f => ({ ...f, search: searchInput || undefined, page: 1 }));
  };

  const handleSort = (field: string) => {
    setFilters(f => ({
      ...f,
      sort_by: field,
      sort_order: f.sort_by === field && f.sort_order === '1' ? '-1' : '1',
    }));
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={cn('h-3 w-3', filters.sort_by === field ? 'text-gray-900' : 'text-gray-400')} />
      </span>
    </th>
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('battery.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('battery.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          title={t('battery.cards.total')}
          value={summary?.total}
          icon={<Battery className="h-5 w-5" />}
          color="blue"
          loading={isLoading}
        />
        <MetricCard
          title={t('battery.cards.critical')}
          value={summary?.critical_count}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
          loading={isLoading}
        />
        <MetricCard
          title={t('battery.cards.low')}
          value={summary?.low_count}
          icon={<BatteryWarning className="h-5 w-5" />}
          color="orange"
          loading={isLoading}
        />
        <MetricCard
          title={t('battery.cards.warning')}
          value={summary?.warning_count}
          icon={<Battery className="h-5 w-5" />}
          color="orange"
          loading={isLoading}
        />
        <MetricCard
          title={t('battery.cards.healthy')}
          value={summary?.healthy_count}
          icon={<BatteryFull className="h-5 w-5" />}
          color="green"
          loading={isLoading}
        />
        <MetricCard
          title={t('battery.cards.externalPower')}
          value={summary?.external_power_count}
          icon={<Zap className="h-5 w-5" />}
          color="purple"
          loading={isLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('battery.charts.voltageDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={isLoading} height={280}>
              <PieChart
                data={voltageDistributionData}
                height={250}
                showLegend
                innerRadius={50}
                colors={['#ef4444', '#f97316', '#eab308', '#22c55e']}
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('battery.charts.baseColorDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={isLoading} height={280}>
              <PieChart
                data={baseColorChartData}
                height={250}
                showLegend
                innerRadius={50}
                colors={baseColorChartColors}
              />
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Voltage Presets */}
            <div className="flex gap-1">
              {(['all', 'critical', 'low', 'warning', 'healthy'] as VoltagePreset[]).map(preset => (
                <Button
                  key={preset}
                  variant={voltagePreset === preset ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyVoltagePreset(preset)}
                  className="text-xs"
                >
                  {t(`battery.presets.${preset}`)}
                </Button>
              ))}
            </div>

            <div className="h-6 w-px bg-gray-200" />

            {/* Base Color Filter */}
            <select
              className="h-8 rounded-md border border-gray-200 bg-white px-3 text-xs"
              value={filters.base_color || ''}
              onChange={e => setFilters(f => ({ ...f, base_color: e.target.value || undefined, page: 1 }))}
            >
              <option value="">{t('battery.filters.allColors')}</option>
              {(summary?.by_base_color || []).map(b => (
                <option key={b.color} value={b.color}>{b.color}</option>
              ))}
            </select>

            {/* Power Source */}
            <select
              className="h-8 rounded-md border border-gray-200 bg-white px-3 text-xs"
              value={filters.power_source || 'all'}
              onChange={e => setFilters(f => ({ ...f, power_source: e.target.value as 'all' | 'battery' | 'external', page: 1 }))}
            >
              <option value="all">{t('battery.filters.allPower')}</option>
              <option value="battery">{t('battery.filters.batteryOnly')}</option>
              <option value="external">{t('battery.filters.externalOnly')}</option>
            </select>

            {/* Status */}
            <select
              className="h-8 rounded-md border border-gray-200 bg-white px-3 text-xs"
              value={filters.status || 'all'}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value as 'all' | 'online' | 'offline', page: 1 }))}
            >
              <option value="all">{t('battery.filters.allStatus')}</option>
              <option value="online">{t('battery.filters.online')}</option>
              <option value="offline">{t('battery.filters.offline')}</option>
            </select>

            {/* Account Filter - Searchable Combobox */}
            <Popover open={accountComboboxOpen} onOpenChange={setAccountComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={accountComboboxOpen}
                  className="h-8 w-[220px] justify-between font-normal text-xs"
                >
                  <span className="truncate">{selectedAccountName}</span>
                  <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0" align="start">
                <Command>
                  <CommandInput placeholder={t('battery.filters.searchAccount')} />
                  <CommandList>
                    <CommandEmpty>
                      {accountsLoading ? 'Loading...' : t('battery.filters.noAccountFound')}
                    </CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => handleAccountChange('all')}
                      >
                        <Check className={cn('mr-2 h-4 w-4', selectedAccountId === 'all' ? 'opacity-100' : 'opacity-0')} />
                        {t('battery.filters.allAccounts')}
                      </CommandItem>
                      {accounts.map(account => (
                        <CommandItem
                          key={account.id}
                          value={account.name}
                          onSelect={() => handleAccountChange(account.id)}
                        >
                          <Check className={cn('mr-2 h-4 w-4', selectedAccountId === account.id ? 'opacity-100' : 'opacity-0')} />
                          {account.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-gray-200" />

            {/* Search */}
            <div className="flex gap-1">
              <Input
                placeholder={t('battery.filters.searchPlaceholder')}
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && applySearch()}
                className="h-8 w-48 text-xs"
              />
              <Button variant="outline" size="sm" onClick={applySearch} className="h-8">
                <Search className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <SortHeader field="name">
                    {t('battery.table.transmitter')}
                  </SortHeader>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('battery.table.account')}
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('battery.table.site')}
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('battery.table.baseColor')}
                  </th>
                  <SortHeader field="battery_voltage">
                    {t('battery.table.voltage')}
                  </SortHeader>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('battery.table.batteryPct')}
                  </th>
                  <SortHeader field="lastpacket_at">
                    {t('battery.table.lastSeen')}
                  </SortHeader>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('battery.table.power')}
                  </th>
                  <SortHeader field="active_flag">
                    {t('battery.table.status')}
                  </SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length > 0 ? (
                  items.map((item: BatteryMonitoringItem) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      {/* Transmitter */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 text-sm">{item.name || item.dev_id}</div>
                        {item.name && (
                          <div className="text-xs text-gray-500">{item.dev_id}</div>
                        )}
                      </td>
                      {/* Account */}
                      <td className="px-4 py-3 text-sm">
                        {item.org_name ? (
                          item.account_id ? (
                            <Link
                              to={`/accounts/${item.account_id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {item.org_name}
                            </Link>
                          ) : (
                            <span className="text-gray-900">{item.org_name}</span>
                          )
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      {/* Site */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.site_name || '--'}
                      </td>
                      {/* Base Color */}
                      <td className="px-4 py-3 text-center">
                        {item.base_color ? (
                          <Badge className={cn('text-xs border', getBaseColorBadgeClass(item.base_color))}>
                            {item.base_color}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-xs">--</span>
                        )}
                      </td>
                      {/* Voltage */}
                      <td className="px-4 py-3 text-sm">
                        <span className={getVoltageColor(item.battery_voltage)}>
                          {item.battery_voltage.toFixed(2)}V
                        </span>
                      </td>
                      {/* Battery % */}
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {item.battery_percentage !== undefined && item.battery_percentage !== null
                          ? `${Math.round(item.battery_percentage)}%`
                          : '--'}
                      </td>
                      {/* Last Seen */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatRelativeTime(item.lastpacket_at)}
                      </td>
                      {/* Power */}
                      <td className="px-4 py-3 text-center">
                        {item.power_external ? (
                          <Badge className="bg-purple-100 text-purple-800 text-xs">
                            <Zap className="h-3 w-3 mr-1" />
                            {t('battery.power.external')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            <Battery className="h-3 w-3 mr-1" />
                            {t('battery.power.battery')}
                          </Badge>
                        )}
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        <Badge className={cn(
                          'text-xs',
                          item.active_flag ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        )}>
                          {item.active_flag ? t('battery.status.online') : t('battery.status.offline')}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <Battery className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>{t('battery.noResults')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-xs text-gray-500">
                {t('battery.pagination.showing', {
                  from: ((pagination.page - 1) * pagination.limit) + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setFilters(f => ({ ...f, page: (f.page || 1) - 1 }))}
                  className="h-7"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <span className="px-3 py-1 text-xs text-gray-600 flex items-center">
                  {pagination.page} / {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() => setFilters(f => ({ ...f, page: (f.page || 1) + 1 }))}
                  className="h-7"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
